import React, { useState } from 'react';
import { Box } from '@mui/system';
import {
  CircularProgress,
  Button,
  Container,
  Alert,
  Snackbar,
  List,
  ListSubheader,
  Typography,
  TextField,
} from '@mui/material';
import { createProduct, deleteProduct, useProducts } from '../../services';
import { User } from '../../types';

const Products = ({ user }: { user: User }) => {
  const { busy, products } = useProducts();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [price, setPrice] = useState('');
  const [orderLink, setOrderLink] = useState('');

  const [saving, setSaving] = useState(false);

  const handleSubmit = async () => {
    setSaving(true);
    createProduct({
      name,
      description,
      imageUrl,
      price: parseFloat(price) || 0,
      orderLink,
      active: true,
    }).then(() => {
      setSaving(false);
      setOpen(true);
      setName('');
      setDescription('');
      setImageUrl('');
      setPrice('');
      setOrderLink('');
    });
  };

  if (!user?.isAdmin) return null;

  return (
    <Container>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
        }}
      >
        <h2>Add a Product</h2>
        <TextField
          label="Name"
          value={name}
          onChange={(x) => setName(x.target.value)}
          style={{ marginBottom: '1rem', minWidth: '400px' }}
          placeholder="Enter product name"
        />
        <TextField
          label="Description"
          value={description}
          onChange={(x) => setDescription(x.target.value)}
          style={{ marginBottom: '1rem', minWidth: '400px' }}
          placeholder="Enter product description"
          multiline
        />
        <TextField
          label="Image URL"
          value={imageUrl}
          onChange={(x) => setImageUrl(x.target.value)}
          style={{ marginBottom: '1rem', minWidth: '400px' }}
          placeholder="Enter image URL"
        />
        <TextField
          label="Price"
          type="number"
          value={price}
          onChange={(x) => setPrice(x.target.value)}
          style={{ marginBottom: '1rem', minWidth: '200px' }}
          placeholder="0.00"
        />
        <TextField
          label="Order Link"
          value={orderLink}
          onChange={(x) => setOrderLink(x.target.value)}
          style={{ marginBottom: '1rem', minWidth: '400px' }}
          placeholder="Enter url to order this product"
        />
        <Button
          variant="contained"
          color="primary"
          startIcon={busy ? <CircularProgress size={20} /> : null}
          disabled={saving || !name}
          sx={{ mt: 1 }}
          onClick={handleSubmit}
        >
          {saving ? 'Saving...' : 'Add Product'}
        </Button>

        <List
          subheader={<ListSubheader sx={{ mt: 5 }}>Products</ListSubheader>}
          sx={{ width: '80%' }}
        >
          {products.map((item) => (
            <Box
              key={item.id}
              sx={{
                p: 1,
                borderBottom: '1px solid #ddd',
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-between',
              }}
            >
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                <Typography>{item.name}</Typography>
                {!!item.price && (
                  <Typography color="GrayText" variant="caption">
                    ${item.price.toFixed(2)}
                  </Typography>
                )}
                {item.orderLink && (
                  <Typography color="GrayText" variant="caption">
                    <a href={item.orderLink} target="_blank" rel="noreferrer">
                      {item.orderLink}
                    </a>
                  </Typography>
                )}
              </Box>
              <div style={{ flex: 1 }}></div>
              <Button onClick={() => deleteProduct(item.id)}>Delete</Button>
            </Box>
          ))}
        </List>
        <Snackbar
          open={open}
          autoHideDuration={6000}
          onClose={() => setOpen(false)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert
            onClose={() => setOpen(false)}
            severity="success"
            sx={{ width: '100%' }}
          >
            Product Added
          </Alert>
        </Snackbar>
      </Box>
    </Container>
  );
};

export default Products;
